/**
 * Centralized Logging System
 * 
 * Provides a unified logging interface with different log levels,
 * formatting, and optional remote logging capabilities.
 */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

class Logger {
  constructor() {
    this.logLevel = process.env.NODE_ENV === 'production' 
      ? LOG_LEVELS.WARN 
      : LOG_LEVELS.DEBUG;
    
    this.logHistory = [];
    this.maxHistorySize = 100;
    this.enableRemoteLogging = process.env.REACT_APP_ENABLE_REMOTE_LOGGING === 'true';
  }

  /**
   * Format log message with timestamp and metadata
   */
  formatMessage(level, message, metadata = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: level.toUpperCase(),
      message,
      ...metadata,
    };
    
    return logEntry;
  }

  /**
   * Add log to history (circular buffer)
   */
  addToHistory(logEntry) {
    this.logHistory.push(logEntry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }
  }

  /**
   * Get formatted console message
   */
  getConsoleMessage(logEntry) {
    const { timestamp, level, message, ...metadata } = logEntry;
    const metaString = Object.keys(metadata).length > 0 
      ? ` ${JSON.stringify(metadata)}` 
      : '';
    return `[${timestamp}] [${level}] ${message}${metaString}`;
  }

  /**
   * Log error messages
   */
  error(message, error = null, metadata = {}) {
    if (LOG_LEVELS.ERROR <= this.logLevel) {
      const logEntry = this.formatMessage('error', message, {
        ...metadata,
        error: error ? {
          message: error.message,
          stack: error.stack,
          name: error.name,
        } : null,
      });

      this.addToHistory(logEntry);
      console.error(this.getConsoleMessage(logEntry));
      
      if (error) {
        console.error(error);
      }

      // In production, you might want to send errors to a logging service
      if (this.enableRemoteLogging && error) {
        this.sendToRemoteLogging(logEntry);
      }
    }
  }

  /**
   * Log warning messages
   */
  warn(message, metadata = {}) {
    if (LOG_LEVELS.WARN <= this.logLevel) {
      const logEntry = this.formatMessage('warn', message, metadata);
      this.addToHistory(logEntry);
      console.warn(this.getConsoleMessage(logEntry));
    }
  }

  /**
   * Log info messages
   */
  info(message, metadata = {}) {
    if (LOG_LEVELS.INFO <= this.logLevel) {
      const logEntry = this.formatMessage('info', message, metadata);
      this.addToHistory(logEntry);
      console.info(this.getConsoleMessage(logEntry));
    }
  }

  /**
   * Log debug messages
   */
  debug(message, metadata = {}) {
    if (LOG_LEVELS.DEBUG <= this.logLevel) {
      const logEntry = this.formatMessage('debug', message, metadata);
      this.addToHistory(logEntry);
      console.log(this.getConsoleMessage(logEntry));
    }
  }

  /**
   * Log API request
   */
  logRequest(method, url, data = null, headers = {}) {
    this.debug('API Request', {
      method,
      url,
      data: data ? (typeof data === 'object' ? JSON.stringify(data) : data) : null,
      headers: Object.keys(headers).reduce((acc, key) => {
        // Don't log sensitive headers
        if (key.toLowerCase() === 'authorization') {
          acc[key] = '***';
        } else {
          acc[key] = headers[key];
        }
        return acc;
      }, {}),
    });
  }

  /**
   * Log API response
   */
  logResponse(method, url, status, data = null, duration = null) {
    const metadata = {
      method,
      url,
      status,
      duration: duration ? `${duration}ms` : null,
    };

    if (status >= 400) {
      this.error('API Response Error', null, { ...metadata, responseData: data });
    } else {
      this.debug('API Response', { ...metadata, hasData: !!data });
    }
  }

  /**
   * Send logs to remote logging service (optional)
   */
  async sendToRemoteLogging(logEntry) {
    // Implement remote logging to your service (e.g., Sentry, LogRocket, etc.)
    // This is a placeholder for future implementation
    try {
      // Example: await fetch('/api/logs', { method: 'POST', body: JSON.stringify(logEntry) });
    } catch (error) {
      console.error('Failed to send log to remote service:', error);
    }
  }

  /**
   * Get log history
   */
  getHistory() {
    return [...this.logHistory];
  }

  /**
   * Clear log history
   */
  clearHistory() {
    this.logHistory = [];
  }

  /**
   * Set log level
   */
  setLogLevel(level) {
    if (typeof level === 'string') {
      this.logLevel = LOG_LEVELS[level.toUpperCase()] || LOG_LEVELS.INFO;
    } else {
      this.logLevel = level;
    }
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;

